import React, { useEffect, useState } from "react";
import { Page } from "@/components/Layout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PhoneCall, CheckCircle, Calendar, Clock, ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import { useAuthModal } from "@/components/AuthModal";

const timeSlots = ["10:00", "11:30", "14:00", "15:30", "17:00"];
const days = Array.from({ length: 14 }, (_, i) => { const d = new Date(); d.setDate(d.getDate() + i + 1); return d; });

export default function FreeConsultation() {
  const { user, loading: authLoading } = useAuth();
  const { open: openAuth } = useAuthModal();
  const [form, setForm] = useState({ name: "", email: "", phone: "", division: "couch", date: "", time_slot: "", reason: "" });
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.name || "",
        email: f.email || user.email || "",
        phone: f.phone || user.phone || "",
      }));
    }
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date) { toast.error("Please pick a date first"); return; }
    if (!form.time_slot) { toast.error("Please pick a time slot"); return; }
    if (!form.phone.trim()) { toast.error("Please add your phone number"); return; }
    try {
      await api.post("/consultations", form);
      setDone(true);
      toast.success("Booked! Confirmation on its way.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not book");
    }
  };

  if (!authLoading && !user) return (
    <Page>
      <section className="md-section"><div className="md-container max-w-xl text-center" data-testid="consult-login-gate">
        <h1 className="text-4xl font-serif mb-3">Please log in to book your free consultation</h1>
        <p className="text-base mb-8" style={{ color: "var(--md-text-soft)" }}>Signing in takes seconds, your details are filled in automatically.</p>
        <button onClick={() => openAuth("login")} className="btn-primary" data-testid="consult-login-btn">Log in / Sign up <ArrowRight size={14} weight="bold" /></button>
      </div></section>
    </Page>
  );

  if (done) return (
    <Page>
      <section className="md-section"><div className="md-container max-w-xl text-center">
        <CheckCircle size={64} weight="duotone" style={{ color: "var(--md-primary)" }} className="mx-auto" />
        <h1 className="text-4xl font-serif mt-6">You're on Shreeja's calendar</h1>
        <p className="mt-3" style={{ color: "var(--md-text-soft)" }}>A confirmation will arrive at {form.email}.</p>
      </div></section>
    </Page>
  );

  return (
    <Page>
      <section className="flow-gradient md-section">
        <div className="md-container max-w-3xl">
          <div className="md-label-eyebrow mb-4">Free Consultation</div>
          <h1 className="text-5xl md:text-6xl font-serif mb-4">20 minutes with Shreeja</h1>
          <p className="text-lg" style={{ color: "var(--md-text-soft)" }}>A free, no-pressure conversation to help you find the right path. One per person.</p>
          <form onSubmit={submit} className="md-card mt-10 space-y-5" data-testid="consultation-form">
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="md-label-eyebrow block mb-2">Name</label><input required className="md-input" value={form.name} readOnly data-testid="consult-name" /></div>
              <div><label className="md-label-eyebrow block mb-2">Email</label><input required type="email" className="md-input" value={form.email} readOnly data-testid="consult-email" /></div>
              <div><label className="md-label-eyebrow block mb-2">Phone *</label><input required className="md-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="consult-phone" /></div>
              <div><label className="md-label-eyebrow block mb-2">Division</label>
                <select className="md-input" value={form.division} onChange={(e) => setForm({ ...form, division: e.target.value })} data-testid="consult-division">
                  <option value="couch">Couch</option><option value="forward">Forward</option>
                </select>
              </div>
            </div>
            <div>
              <label className="md-label-eyebrow block mb-2"><Calendar size={12} className="inline mr-1" />Pick a date</label>
              <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2">
                {days.map((d) => {
                  const ds = d.toISOString().slice(0, 10);
                  return <button type="button" key={ds} onClick={() => setForm({ ...form, date: ds })} className="shrink-0 rounded-lg px-3 py-2 border text-center min-w-[70px]" style={form.date === ds ? { background: "#f4fa9c", color: "#1a411d", borderColor: "#1a411d", fontWeight: 600 } : { borderColor: "var(--md-border)" }} data-testid={`consult-date-${ds}`}>
                    <div className="text-xs opacity-70">{d.toLocaleDateString("en-US", { weekday: "short" })}</div>
                    <div className="text-base font-serif">{d.getDate()}</div>
                  </button>;
                })}
              </div>
            </div>
            <div>
              <label className="md-label-eyebrow block mb-2"><Clock size={12} className="inline mr-1" />Pick a time</label>
              <div className="grid grid-cols-5 gap-2">
                {timeSlots.map((t) => (
                  <button type="button" key={t}
                    onClick={() => {
                      if (!form.date) { toast.error("Please pick a date first"); return; }
                      setForm({ ...form, time_slot: t });
                    }}
                    className="rounded-lg py-2 text-sm border"
                    style={form.time_slot === t ? { background: "#f4fa9c", color: "#1a411d", borderColor: "#1a411d", fontWeight: 600 } : { borderColor: "var(--md-border)", ...(form.date ? {} : { opacity: 0.45, cursor: "not-allowed" }) }}
                    data-testid={`consult-time-${t}`}>{t}</button>
                ))}
              </div>
              {!form.date && (
                <p className="text-xs mt-2" style={{ color: "var(--md-text-muted)" }} data-testid="consult-time-hint">Pick a date first to choose a time.</p>
              )}
            </div>
            <div>
              <label className="md-label-eyebrow block mb-2">What's on your mind (optional)</label>
              <textarea className="md-input" rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="consult-reason" />
            </div>
            <button type="submit" className="btn-primary" data-testid="consult-submit"><PhoneCall size={14} weight="bold" /> Book a Free Consultation</button>
          </form>
        </div>
      </section>
    </Page>
  );
}
